import React, { FC, createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useFeatureOnboardingQuery } from 'generated/sanity-graphql';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isIOS, isWeb } from 'utils/environment';
import { maybeLocaleImage } from 'utils/graphql';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { getLocaleTextBlock } from 'utils/sanity';

import {
  Channel,
  IFeatureOnboarding,
  IOnboardingContext,
  IOnboardingProps,
  TriggerRule,
} from './types';

export const OnboardingContext = createContext<IOnboardingContext>({} as IOnboardingContext);

export const useOnboardingContext = () => useContext<IOnboardingContext>(OnboardingContext);

export const OnboardingProvider: FC = ({ children }: IOnboardingProps) => {
  const [allFeatureOnboarding, setAllFeatureOnboarding] = useState<IFeatureOnboarding[]>([]);

  const onboardingEnabled = useFlag(LaunchDarklyFlag.ENABLE_FEATURE_ONBOARDING);
  const { data, loading } = useFeatureOnboardingQuery({
    skip: !onboardingEnabled,
  });

  useEffect(() => {
    if (data?.allFeatureOnboardings?.length) {
      const featureOnboardings = data.allFeatureOnboardings.map(onboarding => {
        const featureOnboardingChannel = onboarding.channel ?? '';
        const featureOnboardingTriggerRule = onboarding.triggerRule ?? '';
        const screens = onboarding.screens;
        const featureOnboardingScreens = screens?.map(screen => {
          switch (screen?.__typename) {
            case 'ImageWithHeaderAndText':
              return {
                image: maybeLocaleImage(screen.image),
                imageDescription: screen?.imageDescription?.locale ?? '',
                textContent: getLocaleTextBlock(screen, 'textContent'),
                buttonText: screen.buttonAction?.actionText?.locale ?? '',
                buttonUrl: screen.buttonAction?.actionUrl?.locale ?? '',
              };
            default:
              return null;
          }
        });
        return {
          featureOnboardingChannel,
          featureOnboardingTriggerRule,
          featureOnboardingScreens,
        };
      });
      setAllFeatureOnboarding(featureOnboardings);
    }
  }, [data]);

  const firstAppLoadOnboarding = useMemo(() => {
    const channel = isWeb ? Channel.WEB : isIOS() ? Channel.IOS : Channel.ANDROID;

    return (
      allFeatureOnboarding.filter(
        onboarding =>
          onboarding.featureOnboardingChannel === channel &&
          onboarding.featureOnboardingTriggerRule === TriggerRule.ON_FIRST_APP_LOAD
      )[0] || []
    );
  }, [allFeatureOnboarding]);

  const showOnboardingContent =
    !LocalStorage.getItem(StorageKeys.HAS_DONE_FIRST_APP_LOAD_ONBOARDING) &&
    onboardingEnabled &&
    !!firstAppLoadOnboarding?.featureOnboardingScreens?.length;

  return (
    <OnboardingContext.Provider
      value={{
        onboardingEnabled,
        allFeatureOnboarding,
        featureOnboardingLoading: loading,
        showOnboardingContent,
        firstAppLoadOnboarding: firstAppLoadOnboarding?.featureOnboardingScreens ?? [],
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
